import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  createAwarenessEvent,
  getAwarenessEventById,
  updateAwarenessEvent,
  getAwarenessSections,
  updateAwarenessSection,
  createAwarenessSection,
  AwarenessContentRow
} from '../../../services/Writer/awarenessService';
import { AwarenessEditorBlock } from './AwarenessEditorBlock';
import { LivePreview } from './LivePreview';


type EventType = 'health' | 'seasonal';
type EventSubtype = 'day' | 'week' | 'month';

const toDateInput = (value?: string | null) => {
  if (!value) return '';
  const raw = String(value);
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
};

const toSubtypePriority = (subtype: EventSubtype): number => {
  if (subtype === 'day') return 100;
  if (subtype === 'week') return 80;
  return 60;
};

const parseActiveValue = (value: unknown): boolean => {
  return value === true || value === 1 || value === '1';
};



const AwarenessFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { event_id } = useParams<{ event_id: string }>();
  const isEdit = Boolean(event_id);

  const [eventId, setEventId] = useState('');
  const [eventType, setEventType] = useState<EventType>('health');
  const [eventSubtype, setEventSubtype] = useState<EventSubtype>('day');
  const [title, setTitle] = useState('');
  const [theme, setTheme] = useState('');
  const [shortDescription, setShortDescription] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [priority, setPriority] = useState<number>(100);
  const [active, setActive] = useState(true);
  const [region, setRegion] = useState('Global');
  const [tags, setTags] = useState('');
  const [sections, setSections] = useState<AwarenessContentRow[]>([]);

  const [featuredImageFile, setFeaturedImageFile] = useState<File | null>(null);
  const [existingImage, setExistingImage] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [priorityTouched, setPriorityTouched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(true);

  useEffect(() => {
    if (!isEdit || !event_id) return;

    const loadEvent = async () => {
      try {
        setLoading(true);

        const data = await getAwarenessEventById(event_id);
        const sectionData = await getAwarenessSections(event_id);

        if (sectionData && sectionData.length > 0) {
          setSections(sectionData);
        } else {
          setSections([
            { id: 0, event_id, section: "what", content: "", display_order: 1, status: "draft" },
            { id: 0, event_id, section: "why", content: "", display_order: 2, status: "draft" },
            { id: 0, event_id, section: "prevention", content: "", display_order: 3, status: "draft" },
            { id: 0, event_id, section: "faq", content: "", display_order: 4, status: "draft" },
            { id: 0, event_id, section: "summary", content: "", display_order: 5, status: "draft" }
          ]);
        }

        if (!data) {
          toast.error("Event not found");
          return;
        }

        setEventId(data.event_id ?? event_id);
        setEventType((data.event_type ?? "health") as EventType);
        setEventSubtype((data.event_subtype ?? "day") as EventSubtype);

        setTitle(data.title ?? "");
        setTheme(data.theme ?? "");
        setShortDescription(data.short_description ?? "");

        setStartDate(data.start_date ? toDateInput(data.start_date) : "");
        setEndDate(data.end_date ? toDateInput(data.end_date) : "");

        setPriority(
          typeof data.priority === "number"
            ? data.priority
            : Number(data.priority ?? toSubtypePriority((data.event_subtype ?? "day") as EventSubtype))
        );

        setActive(parseActiveValue(data.active));
        setRegion(data.region ?? "Global");

        if (Array.isArray(data.tags)) {
          setTags(data.tags.join(", "));
        } else {
          setTags(data.tags ?? "");
        }

        setExistingImage(data.featured_image ?? "");

      } catch (error: any) {
        toast.error(error?.response?.data?.message || "Failed to load event");
      } finally {
        setLoading(false);
      }
    };

    loadEvent();
  }, [isEdit, event_id]);

  useEffect(() => {
    if (!isEdit) {
      setSections([
        { id: 0, event_id: '', section: "what", content: "", display_order: 1, status: "draft" },
        { id: 0, event_id: '', section: "why", content: "", display_order: 2, status: "draft" },
        { id: 0, event_id: '', section: "prevention", content: "", display_order: 3, status: "draft" },
        { id: 0, event_id: '', section: "faq", content: "", display_order: 4, status: "draft" },
        { id: 0, event_id: '', section: "summary", content: "", display_order: 5, status: "draft" }
      ]);
    }
  }, [isEdit]);

  useEffect(() => {
    if (!priorityTouched) {
      setPriority(toSubtypePriority(eventSubtype));
    }
  }, [eventSubtype, priorityTouched]);

  const canSubmit = useMemo(
    () =>
      Boolean(
        eventId.trim() && eventType && eventSubtype && title.trim() && startDate && endDate
      ),
    [eventId, eventType, eventSubtype, title, startDate, endDate]
  );

  const imagePreviewUrl = useMemo(() => {
    if (featuredImageFile) {
      return URL.createObjectURL(featuredImageFile);
    }
    return existingImage;
  }, [featuredImageFile, existingImage]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!eventId.trim()) return toast.error('Event ID is required');
    if (!eventType) return toast.error('Event Type is required');
    if (!eventSubtype) return toast.error('Event Subtype is required');
    if (!title.trim()) return toast.error('Title is required');
    if (!startDate) return toast.error('Start Date is required');
    if (!endDate) return toast.error('End Date is required');

    const fd = new FormData();
    fd.append('event_id', eventId.trim());
    fd.append('event_type', eventType);
    fd.append('event_subtype', eventSubtype);
    fd.append('title', title.trim());
    fd.append('theme', theme.trim());
    fd.append('short_description', shortDescription.trim());
    fd.append('start_date', startDate);
    fd.append('end_date', endDate);
    fd.append('priority', String(priority));
    fd.append('active', active ? '1' : '0');
    fd.append('region', region.trim() || 'Global');
    fd.append('tags', tags.trim());

    if (featuredImageFile) {
      fd.append('featured_image', featuredImageFile);
    }

    try {
      setSubmitting(true);
      if (isEdit && event_id) {
        await updateAwarenessEvent(event_id, fd);
        for (const section of sections) {
          if (section.id && section.id !== 0) {
            await updateAwarenessSection(section.id, {
              content: section.content
            });
          } else {
            await createAwarenessSection({
              event_id: eventId,
              event_type: eventType,
              section: section.section,
              content: section.content,
              display_order: section.display_order,
              status: "published"
            });
          }
        }
        toast.success('Event updated successfully');
      } else {
        await createAwarenessEvent(fd);
        for (const section of sections) {
          await createAwarenessSection({
            event_id: eventId,
            event_type: eventType,
            section: section.section,
            content: section.content,
            display_order: section.display_order,
            status: "published"
          });
        }
        toast.success('Event created successfully');
      }
      setTimeout(() => navigate('/writer/awareness'), 500);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || e?.message || 'Save failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="p-10 text-center text-lg">
        Loading awareness event...
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4">
      <ToastContainer position="top-right" autoClose={2500} />
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        <div className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 shadow-sm transition-all ${
          showPreview ? 'lg:col-span-6' : 'lg:col-span-12'
        }`}>
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {isEdit ? 'Edit Awareness Event' : 'Create Awareness Event'}
            </h1>
            {!showPreview && (
              <button
                type="button"
                onClick={() => setShowPreview(true)}
                className="px-4 py-2 text-xs font-bold text-primary-600 dark:text-primary-400 border border-primary-300 dark:border-primary-700 rounded-lg hover:bg-primary-50 dark:hover:bg-primary-900/30 transition"
              >
                Show Preview
              </button>
            )}
          </div>

          <form onSubmit={onSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium mb-2">Event ID *</label>
              <input
                value={eventId}
                onChange={(e) => setEventId(e.target.value)}
                placeholder="e.g. WHD_WORLD_GLAUCOMA_DAY_2026"
                disabled={isEdit}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 disabled:opacity-70"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Event Type *</label>
                <select
                  value={eventType}
                  onChange={(e) => setEventType(e.target.value as EventType)}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900"
                >
                  <option value="health">Health</option>
                  <option value="seasonal">Seasonal</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Event Subtype *</label>
                <select
                  value={eventSubtype}
                  onChange={(e) => setEventSubtype(e.target.value as EventSubtype)}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900"
                >
                  <option value="day">Day</option>
                  <option value="week">Week</option>
                  <option value="month">Month</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Title *</label>
                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="World Glaucoma Week"
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Theme</label>
                <input
                  value={theme}
                  onChange={(e) => setTheme(e.target.value)}
                  placeholder=""
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900"
                />
              </div>
            </div>

            <AwarenessEditorBlock
              label="Short Description"
              value={shortDescription}
              onChange={setShortDescription}
              placeholder="Brief summary shown in listings/slides"
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Start Date *</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => {
                    const formatted = e.target.value;
                    setStartDate(formatted);
                  }}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">End Date *</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => {
                    const formatted = e.target.value;
                    setEndDate(formatted);
                  }}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Priority</label>
                <input
                  type="number"
                  min={60}
                  max={100}
                  step={20}
                  value={priority}
                  onChange={(e) => {
                    setPriorityTouched(true);
                    const val = Number(e.target.value);
                    setPriority(val);
                  }}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900"
                />
                <p className="text-xs text-gray-500 mt-1">Recommended: day=100, week=80, month=60</p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Active</label>
                <select
                  value={active ? 'true' : 'false'}
                  onChange={(e) => setActive(e.target.value === 'true')}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900"
                >
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Region</label>
                <input
                  value={region}
                  onChange={(e) => setRegion(e.target.value)}
                  placeholder="Global"
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Tags</label>
                <input
                  value={tags}
                  onChange={(e) => setTags(e.target.value)}
                  placeholder="eyes, glaucoma, awareness"
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Featured Image</label>
              {existingImage ? (
                <div className="mb-3">
                  <img
                    src={existingImage}
                    alt="Existing"
                    className="h-40 rounded-lg border border-gray-200 dark:border-gray-700"
                  />
                </div>
              ) : null}
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setFeaturedImageFile(e.target.files?.[0] || null)}
                className="block w-full text-sm"
              />
            </div>

            <div className="space-y-6 mt-10">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-800 pb-2">
                Long Description
              </h2>

              {sections.map((section, index) => (
                <AwarenessEditorBlock
                  key={section.id || index}
                  label={section.section}
                  value={section.content}
                  onChange={(newVal) => {
                    const updated = [...sections];
                    updated[index].content = newVal;
                    setSections(updated);
                  }}
                  placeholder={`Write content for ${section.section}...`}
                />
              ))}
            </div>

            <div className="flex justify-end gap-3 pt-6">
              <button
                type="button"
                onClick={() => navigate('/writer/awareness')}
                className="px-5 py-2.5 rounded-lg bg-gray-200 dark:bg-gray-700"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting || !canSubmit}
                className="px-5 py-2.5 rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-60"
              >
                {submitting ? 'Saving...' : isEdit ? 'Update' : 'Create'}
              </button>
            </div>
          </form>
        </div>

        {showPreview && (
          <div className="lg:col-span-6 lg:sticky lg:top-6">
            <LivePreview
              title={title}
              theme={theme}
              shortDescription={shortDescription}
              sections={sections}
              eventType={eventType}
              startDate={startDate}
              endDate={endDate}
              region={region}
              tags={tags}
              imagePreviewUrl={imagePreviewUrl}
              onClose={() => setShowPreview(false)}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default AwarenessFormPage;